import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  timeLogAPI,
  timelogCorrectionAPI,
  autoClockOutAPI,
} from "../../services/api";
import { useAuth } from "../AuthContext";
import {
  formatElapsedSince,
  formatStudioTime,
} from "../../utils/workforceTimeUtils";
import { isAdminRole } from "../../utils/workforceUtils";
import StaffLayout from "./StaffLayout";
import CorrectionRequestQueue from "./CorrectionRequestQueue";
import RecentAutoClockOuts from "./RecentAutoClockOuts";
import "./DailyReviewDashboard.css";

/**
 * Workforce — Daily Review Dashboard (TIME-009).
 *
 * Admin-only "who's clocked in right now" live view. Polls the backend every
 * 30s and renders a scannable table of currently-clocked-in staff with name,
 * role/jobCode, punch-in time, live-updating elapsed duration, break status,
 * and source. Filter row supports name/role/email search + role dropdown.
 *
 * Authoritative inputs:
 *   - docs/design/TIME-009-daily-review-dashboard-design.md (Zuri)
 *   - docs/backlog/TIME-009-daily-review-dashboard.md (story file)
 *   - docs/design/_workforce-design-system.md (light-mode admin palette)
 *   - frontend/src/components/staff/ClockInOutScreen.js (polling pattern)
 *
 * Scope per Web Sprint 1 (2.5 pt):
 *   - Default ships the table view (Phase A); the spec's compact-card +
 *     density toggle is a deferred ride-along (sprint-plan note).
 *   - Polling only — no websocket (story Out of Scope).
 *   - Manual override modal + side drawer are deferred (TIME-010 / future).
 *   - Status summary tiles + "Attention Needed" / "Not Yet Clocked In" rails
 *     described in the design spec are Phase B targets — Phase A renders a
 *     single "Currently Clocked In" rail (Scenario 1 of the story file).
 *
 * RBAC: admin-only. Route-level ProtectedRoute gates entry; this component
 * also re-checks user role and renders an access-denied panel if a non-admin
 * lands here (defense in depth — matches StaffProfileList convention).
 *
 * Backend contract per Simone's 2026-04-28 announcement:
 *   GET /api/workforce/timelogs/currently-clocked-in
 *   200 → { timeLogs: [...], asOf: <ISO> } OR an array; reader is shape-
 *   tolerant. Each entry exposes: { _id, punchInTime, jobCode, source,
 *   currentBreak: { startTime } | null, staff: { firstName, lastName,
 *   email, primaryRole } }. If the live shape diverges, the reader normalises
 *   inside `normaliseRow` so the UI stays consistent.
 */

const POLL_INTERVAL_MS = 30_000;

// Tick the live elapsed-duration display once a minute (the format is
// "HHh MMm", so anything finer is a no-op DOM diff). 60s cadence keeps SR
// announcements quiet while remaining within a minute of accuracy.
const LIVE_TICKER_MS = 60_000;

// TIME-010 §4.1 + TIME-007 §4.1 — tab IDs for the dashboard tab-set. v1
// ships three tabs (currently-clocked-in + pending-corrections + recent
// auto-clock-outs). Tab pattern per workforce design system §6.
const TABS = {
  CURRENTLY_CLOCKED_IN: "currently_clocked_in",
  PENDING_CORRECTIONS: "pending_corrections",
  RECENT_AUTO_CLOCK_OUTS: "recent_auto_clock_outs",
};

/**
 * Best-effort row normaliser. We accept the backend-canonical shape AND
 * a couple of tolerated legacy shapes so we never break the dashboard if
 * the response wrapper changes mid-rollout. The contract source of truth
 * is the design spec + Simone's announcement — this is defensive only.
 */
const normaliseRow = (row) => {
  if (!row || typeof row !== "object") return null;
  const id = row._id || row.id || row.timeLogId;
  if (!id) return null;

  // Backend `getCurrentlyClockedIn` reshapes the response with two distinct
  // sub-objects per row: `row.user` (firstName/lastName/email/_id) and
  // `row.staffProfile` (primaryRole/employmentType/_id). Older Mongoose-
  // populate shapes (`row.userId` / `row.staffProfileId`) and legacy
  // (`row.staff`) preserved as graceful-degradation fallbacks.
  const userObj =
    (row.user && typeof row.user === "object" && row.user) ||
    (row.userId && typeof row.userId === "object" && row.userId) ||
    null;
  const staffProfileObj =
    (row.staffProfile && typeof row.staffProfile === "object" && row.staffProfile) ||
    (row.staffProfileId && typeof row.staffProfileId === "object" && row.staffProfileId) ||
    (row.staff && typeof row.staff === "object" && row.staff) ||
    null;
  const firstName =
    userObj?.firstName || staffProfileObj?.firstName || row.firstName || "";
  const lastName =
    userObj?.lastName || staffProfileObj?.lastName || row.lastName || "";
  const email = userObj?.email || staffProfileObj?.email || row.email || "";
  const role =
    row.jobCode ||
    staffProfileObj?.primaryRole ||
    staffProfileObj?.role ||
    row.role ||
    "";

  const fullName = `${firstName} ${lastName}`.trim() || email || "(unknown)";

  const currentBreak =
    row.currentBreak && row.currentBreak.startTime
      ? { startTime: row.currentBreak.startTime }
      : null;

  return {
    id,
    fullName,
    firstName,
    lastName,
    email,
    role,
    punchInTime: row.punchInTime || row.startTime || null,
    source: row.source || row.clientType || "mobile",
    currentBreak,
    raw: row,
  };
};

const DailyReviewDashboard = () => {
  const { user } = useAuth();
  const adminAccess = isAdminRole(user?.role);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pollError, setPollError] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  // Re-render handle for the live elapsed-duration ticker; the formatted
  // string only changes on minute boundaries so the DOM diff is mostly a
  // no-op, but the state bump lets React re-evaluate the elapsed text.
  const [, setLiveTick] = useState(0);
  const intervalRef = useRef(null);
  const tickerRef = useRef(null);

  // TIME-010 §4.1 + TIME-007 §4.1 — active tab + count badges.
  // Both Pending Corrections (TIME-010) and Recent Auto-Clock-Outs (TIME-007)
  // poll on the same 30s cadence as the rest of the dashboard (Zuri spec
  // §4.1). Counted independently of which tab is active so badges stay
  // fresh in the background. Recent uses days=7 default to match the
  // table-side default range.
  const [activeTab, setActiveTab] = useState(TABS.CURRENTLY_CLOCKED_IN);
  const [pendingCount, setPendingCount] = useState(0);
  const pendingPollRef = useRef(null);
  const [actionToast, setActionToast] = useState(null);
  const [autoClockOutCount, setAutoClockOutCount] = useState(0);
  const autoClockOutPollRef = useRef(null);

  /**
   * Fetch currently-clocked-in staff. Tolerant of multiple response shapes
   * (`{ timeLogs: [] }`, `{ data: [] }`, bare array). Sets the rendering
   * state and the last-updated stamp on success. On failure: keep the prior
   * row set on screen and surface a quiet stale-banner — same pattern as
   * ClockInOutScreen's pollError handling.
   */
  const refresh = useCallback(async () => {
    if (!adminAccess) return;
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (roleFilter) params.role = roleFilter;

      const response = await timeLogAPI.getCurrentlyClocked(params);
      const payload = response?.data;
      // Backend returns { currentlyClockedIn, count, asOf }. Legacy shapes
      // (`timeLogs`, `data`, bare array) kept as graceful-degradation chain.
      const list =
        (payload &&
          Array.isArray(payload.currentlyClockedIn) &&
          payload.currentlyClockedIn) ||
        (payload && Array.isArray(payload.timeLogs) && payload.timeLogs) ||
        (payload && Array.isArray(payload.data) && payload.data) ||
        (Array.isArray(payload) ? payload : []);

      const normalised = list
        .map(normaliseRow)
        .filter((r) => r !== null);

      setRows(normalised);
      setLastUpdatedAt(new Date());
      setPollError(false);
    } catch (err) {
      // Leave prior rows on screen; surface a quiet stale banner. Don't
      // toast on every poll failure (would spam during a backend hiccup).
      setPollError(true);
    } finally {
      setLoading(false);
    }
  }, [adminAccess, searchTerm, roleFilter]);

  // Mount: initial fetch + 30s poll + visibility-aware pause (mirrors
  // ClockInOutScreen's TIME-004a pattern; tech doc §6.5 for that screen).
  useEffect(() => {
    if (!adminAccess) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;

    const startPolling = () => {
      if (intervalRef.current) return;
      intervalRef.current = setInterval(() => {
        if (!cancelled) refresh();
      }, POLL_INTERVAL_MS);
    };
    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    const onVisibilityChange = () => {
      if (typeof document === "undefined") return;
      if (document.visibilityState === "visible") {
        refresh();
        startPolling();
      } else {
        stopPolling();
      }
    };

    refresh();
    startPolling();
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibilityChange);
    }
    return () => {
      cancelled = true;
      stopPolling();
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibilityChange);
      }
    };
  }, [adminAccess, refresh]);

  // Live elapsed-duration ticker — fires once a minute (cheap; DOM diff is a
  // no-op except on minute boundaries). Independent of the data poll so the
  // displayed durations remain fresh even between polls.
  useEffect(() => {
    if (!adminAccess) return undefined;
    tickerRef.current = setInterval(() => {
      setLiveTick((n) => n + 1);
    }, LIVE_TICKER_MS);
    return () => {
      if (tickerRef.current) clearInterval(tickerRef.current);
    };
  }, [adminAccess]);

  // TIME-010 §4.1 — Pending Corrections count poll. Fires on the same 30s
  // cadence as the rest of the dashboard. Shape-tolerant of the
  // `{ corrections, count, truncated }` envelope from Marcus tech doc §4.2;
  // `count` is convenience and equals corrections.length so we read whichever
  // is available. Failures are silent — the badge stays at its last known
  // value rather than zero-flickering.
  useEffect(() => {
    if (!adminAccess) return undefined;
    let cancelled = false;
    const refreshPending = async () => {
      try {
        const res = await timelogCorrectionAPI.list({ status: "Pending" });
        if (cancelled) return;
        const data = res?.data || {};
        const list = Array.isArray(data.corrections) ? data.corrections : [];
        const count =
          typeof data.count === "number" ? data.count : list.length;
        setPendingCount(count);
      } catch (e) {
        // Silent — keep last known value. Sentry breadcrumb covered by
        // global axios interceptor (OBSERVABILITY-001).
      }
    };
    refreshPending();
    pendingPollRef.current = setInterval(refreshPending, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (pendingPollRef.current) clearInterval(pendingPollRef.current);
    };
  }, [adminAccess]);

  // TIME-007 §4.1 — Recent Auto-Clock-Outs count poll. Same 30s cadence as
  // the rest of the dashboard. Marcus tech doc §5.3 returns
  // `{ autoClockOutEvents, count, ... }`; reader prefers `count` and falls
  // back to events.length. Failures are silent — badge keeps last known value.
  useEffect(() => {
    if (!adminAccess) return undefined;
    let cancelled = false;
    const refreshCount = async () => {
      try {
        const res = await autoClockOutAPI.getRecent({ days: 7 });
        if (cancelled) return;
        const data = res?.data || {};
        const list = Array.isArray(data.autoClockOutEvents)
          ? data.autoClockOutEvents
          : [];
        const count = typeof data.count === "number" ? data.count : list.length;
        setAutoClockOutCount(count);
      } catch (e) {
        // silent — keep last known
      }
    };
    refreshCount();
    autoClockOutPollRef.current = setInterval(refreshCount, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (autoClockOutPollRef.current) clearInterval(autoClockOutPollRef.current);
    };
  }, [adminAccess]);

  // Distinct role values for the role filter dropdown — derived from the
  // current row set so the dropdown only ever lists roles actually in play.
  const roleOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      if (r.role) set.add(r.role);
    });
    return Array.from(set).sort();
  }, [rows]);

  // Client-side filter pass: belt-and-suspenders against the server-side
  // params (the server may or may not honour the search/role params; the
  // client filter ensures the visible list matches the controls regardless).
  const filteredRows = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    return rows.filter((r) => {
      if (roleFilter && r.role !== roleFilter) return false;
      if (!needle) return true;
      const haystack = [r.fullName, r.email, r.role]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [rows, searchTerm, roleFilter]);

  const lastUpdatedLabel = useMemo(() => {
    if (!lastUpdatedAt) return "—";
    return formatStudioTime(lastUpdatedAt);
  }, [lastUpdatedAt]);

  // ----- RBAC defense-in-depth -----
  if (!adminAccess) {
    return (
      <StaffLayout>
        <div className="daily-review-container">
          <h1>Workforce — Daily Review</h1>
          <p className="daily-review-rbac-warning" role="alert">
            You do not have permission to view this dashboard. Contact an
            administrator if you need access.
          </p>
        </div>
      </StaffLayout>
    );
  }

  // ----- Render -----
  return (
    <StaffLayout>
      <div className="daily-review-container">
        <header className="daily-review-header">
          <div>
            <h1>Daily Review</h1>
            <p className="daily-review-subtitle">
              Currently clocked-in staff — live view (refreshes every 30s)
            </p>
          </div>
          <div
            className="daily-review-refresh-chip"
            data-testid="daily-review-refresh-chip"
            aria-live="polite"
          >
            <span className="daily-review-refresh-label">
              Last updated {lastUpdatedLabel}
            </span>
            <button
              type="button"
              className="daily-review-refresh-button"
              onClick={refresh}
              aria-label="Refresh now"
              data-testid="daily-review-refresh-button"
            >
              Refresh
            </button>
          </div>
        </header>

        {pollError && (
          <p
            className="daily-review-stale-banner"
            role="alert"
            data-testid="daily-review-stale-banner"
          >
            We are having trouble reaching the server. Showing the last known
            state.
          </p>
        )}

        {/* TIME-010 §4.1 + TIME-007 §4.1 — tab navigation. v1 ships three
            tabs: Currently Clocked In, Pending Corrections (TIME-010),
            Recent Auto-Clock-Outs (TIME-007). Tab pattern per workforce
            design system §6. */}
        <div
          className="daily-review-tabs"
          role="tablist"
          aria-label="Daily review surfaces"
        >
          <button
            type="button"
            role="tab"
            id="dr-tab-currently-clocked-in"
            aria-selected={activeTab === TABS.CURRENTLY_CLOCKED_IN}
            aria-controls="dr-panel-currently-clocked-in"
            tabIndex={activeTab === TABS.CURRENTLY_CLOCKED_IN ? 0 : -1}
            className={`daily-review-tab ${
              activeTab === TABS.CURRENTLY_CLOCKED_IN ? "is-active" : ""
            }`}
            onClick={() => setActiveTab(TABS.CURRENTLY_CLOCKED_IN)}
            data-testid="daily-review-tab-currently-clocked-in"
          >
            Currently Clocked In
          </button>
          <button
            type="button"
            role="tab"
            id="dr-tab-pending-corrections"
            aria-selected={activeTab === TABS.PENDING_CORRECTIONS}
            aria-controls="dr-panel-pending-corrections"
            tabIndex={activeTab === TABS.PENDING_CORRECTIONS ? 0 : -1}
            className={`daily-review-tab ${
              activeTab === TABS.PENDING_CORRECTIONS ? "is-active" : ""
            }`}
            onClick={() => setActiveTab(TABS.PENDING_CORRECTIONS)}
            data-testid="daily-review-tab-pending-corrections"
          >
            Pending Corrections ({pendingCount})
          </button>
          <button
            type="button"
            role="tab"
            id="dr-tab-recent-auto-clock-outs"
            aria-selected={activeTab === TABS.RECENT_AUTO_CLOCK_OUTS}
            aria-controls="dr-panel-recent-auto-clock-outs"
            tabIndex={activeTab === TABS.RECENT_AUTO_CLOCK_OUTS ? 0 : -1}
            className={`daily-review-tab ${
              activeTab === TABS.RECENT_AUTO_CLOCK_OUTS ? "is-active" : ""
            }`}
            onClick={() => setActiveTab(TABS.RECENT_AUTO_CLOCK_OUTS)}
            data-testid="daily-review-tab-recent-auto-clock-outs"
          >
            Recent Auto-Clock-Outs ({autoClockOutCount})
          </button>
        </div>

        {activeTab === TABS.PENDING_CORRECTIONS ? (
          <div
            role="tabpanel"
            id="dr-panel-pending-corrections"
            aria-labelledby="dr-tab-pending-corrections"
          >
            <CorrectionRequestQueue
              onActionToast={(toast) => {
                setActionToast(toast);
                if (toast?.kind === "success") {
                  // Auto-dismiss success after 4s per workforce design system.
                  setTimeout(() => setActionToast(null), 4000);
                }
              }}
            />
          </div>
        ) : activeTab === TABS.RECENT_AUTO_CLOCK_OUTS ? (
          <div
            role="tabpanel"
            id="dr-panel-recent-auto-clock-outs"
            aria-labelledby="dr-tab-recent-auto-clock-outs"
          >
            <RecentAutoClockOuts />
          </div>
        ) : (
          <div
            role="tabpanel"
            id="dr-panel-currently-clocked-in"
            aria-labelledby="dr-tab-currently-clocked-in"
          >
        <div className="daily-review-filters">
          <input
            type="search"
            className="daily-review-search"
            placeholder="Search by name, email, or role"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Search currently-clocked-in staff"
            data-testid="daily-review-search"
          />
          <label className="daily-review-filter-label">
            <span className="daily-review-filter-label-text">Role</span>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              aria-label="Filter by role"
              data-testid="daily-review-role-filter"
            >
              <option value="">All roles</option>
              {roleOptions.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </label>
        </div>

        {loading ? (
          <p
            className="daily-review-loading"
            data-testid="daily-review-loading"
          >
            Loading currently-clocked-in staff...
          </p>
        ) : filteredRows.length === 0 ? (
          <div
            className="daily-review-empty"
            data-testid="daily-review-empty"
            role="status"
          >
            <p className="daily-review-empty-headline">
              No staff currently clocked in.
            </p>
            <p className="daily-review-empty-detail">
              When staff clock in via the mobile-web Clock screen, they will
              appear here automatically.
            </p>
          </div>
        ) : (
          <div className="daily-review-table-wrap">
            <table
              className="daily-review-table"
              data-testid="daily-review-table"
            >
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Role</th>
                  <th scope="col">Punched in</th>
                  <th scope="col">Elapsed</th>
                  <th scope="col">Break</th>
                  <th scope="col">Source</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => {
                  const onBreak = !!r.currentBreak;
                  const elapsed = r.punchInTime
                    ? formatElapsedSince(r.punchInTime)
                    : "—";
                  const punchedInLabel = r.punchInTime
                    ? formatStudioTime(r.punchInTime)
                    : "—";
                  const breakLabel = onBreak
                    ? `On break since ${formatStudioTime(
                        r.currentBreak.startTime
                      )}`
                    : "—";
                  return (
                    <tr
                      key={r.id}
                      className={`daily-review-row ${
                        onBreak
                          ? "daily-review-row--on-break"
                          : "daily-review-row--clocked-in"
                      }`}
                      data-testid={`daily-review-row-${r.id}`}
                    >
                      <td>
                        <span className="daily-review-status-icon" aria-hidden>
                          {onBreak ? "⏸" : "●"}
                        </span>
                        <span className="daily-review-name">{r.fullName}</span>
                      </td>
                      <td>{r.role || "—"}</td>
                      <td className="daily-review-mono">{punchedInLabel}</td>
                      <td className="daily-review-mono">{elapsed}</td>
                      <td>
                        {onBreak ? (
                          <span className="daily-review-break-pill">
                            {breakLabel}
                          </span>
                        ) : (
                          <span className="daily-review-muted">—</span>
                        )}
                      </td>
                      <td>{r.source}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <p className="daily-review-footnote">
          Times shown in MST. Polling every 30 seconds. The compact-card
          density toggle and manual-override actions are planned for a future
          Phase B story.
        </p>
          </div>
        )}

        {/* TIME-010 §6.4 action toast — surfaces success / error from the
            queue's Approve / Reject flow. */}
        {actionToast && (
          <div
            className={`daily-review-action-toast daily-review-action-toast-${
              actionToast.kind || "info"
            }`}
            role="status"
            data-testid="daily-review-action-toast"
            onClick={() => setActionToast(null)}
          >
            <span>{actionToast.message}</span>
            {actionToast.link && actionToast.link.label && (
              <a
                className="daily-review-action-toast-link"
                href={actionToast.link.href || "#"}
              >
                {actionToast.link.label}
              </a>
            )}
          </div>
        )}
      </div>
    </StaffLayout>
  );
};

export default DailyReviewDashboard;
